import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { formatInTimeZone } from 'date-fns-tz';
import {
  listCampaniasLevantamiento, listMercadosLevantamiento,
  registrarJornadaLevantamiento, type MercadoLevantamiento,
} from '../../api/levantamiento';
import { cierreLocalDe, enqueueJornada, flushJornadas } from '../../offline/jornadaQueue';
import { useAuth } from '../../components/AuthProvider';
import {
  AFLUENCIAS, FACTORES_EXTERNOS, MERCADO_OTRO, cierreVacio, claveDeMercado,
  loQueFaltaEnCierre, type BorradorCierre,
} from '../../domain/levantamiento';


const TZ = 'America/Lima';

function Opcion(
  { activo, onClick, children }:
  { activo: boolean; onClick: () => void; children: React.ReactNode },
) {
  return (
    <button type="button" onClick={onClick} aria-pressed={activo}
      className={`min-h-[44px] rounded border px-3 py-2 text-left text-sm ${
        activo ? 'border-blue-600 bg-blue-50 font-medium text-blue-900' : 'border-gray-300'
      }`}>
      {children}
    </button>
  );
}

function Bloque({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section className="mb-5 rounded-lg border bg-white p-4">
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
        {titulo}
      </h2>
      {children}
    </section>
  );
}

/**
 * Cierre de jornada: el cuestionario APARTE con el que se termina la visita a un mercado.
 *
 * Uno por mercado y por día, no por puesto. Si el auditor hace dos mercados el mismo día, cierra
 * dos veces — son dos visitas distintas.
 *
 * Es el reemplazo **estructurado** de las notas cualitativas que el equipo ya escribe a mano: lo
 * mismo que observan, pero comparable entre días y entre mercados. Sin esto, "el lunes estuvo
 * flojo" es una frase en un WhatsApp; con esto es una fila que se cruza contra los canjes.
 *
 * Se llena todavía dentro del mercado, así que también aguanta sin señal: si no se puede enviar,
 * queda en la cola local y sube solo. Reenviarlo REEMPLAZA el del día en vez de acumular dos
 * cierres que se contradicen.
 */
export default function CierreJornada() {
  const [params] = useSearchParams();
  /** Ver `FichaPuesto`: los roles deciden si el banco de pruebas entra en la lista de campañas. */
  const { roles, loading: cargandoSesion } = useAuth();
  const rolesKey = roles.join(',');
  const fecha = formatInTimeZone(new Date(), TZ, 'yyyy-MM-dd');
  const [campaignId, setCampaignId] = useState('');
  const [mercados, setMercados] = useState<MercadoLevantamiento[]>([]);
  const [pdvId, setPdvId] = useState(
    params.get('pdv') || (params.get('libre') ? MERCADO_OTRO : ''),
  );
  const [mercadoLibre, setMercadoLibre] = useState(params.get('libre') ?? '');
  const [c, setC] = useState<BorradorCierre>(cierreVacio());
  const [cargando, setCargando] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');

  // Se espera a la sesión antes de pedir campañas: sin roles cargados, la lista se armaría como si
  // fuera una cuenta de campo y este efecto no volvería a correr para arreglarla.
  useEffect(() => {
    if (cargandoSesion) return;
    let vivo = true;
    (async () => {
      try {
        const cs = await listCampaniasLevantamiento(roles);
        if (!vivo) return;
        if (cs.length > 0) {
          setCampaignId(cs[0].id);
          const mc = await listMercadosLevantamiento(cs[0].id);
          if (!vivo) return;
          setMercados(mc);
        }
      } catch (e) {
        if (vivo) setError('No se pudo cargar: ' + (e instanceof Error ? e.message : String(e)));
      } finally {
        if (vivo) setCargando(false);
      }
    })();
    return () => { vivo = false; };
    // `rolesKey` y no `roles`: el arreglo es nuevo en cada render y dispararía el efecto sin parar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cargandoSesion, rolesKey]);

  // Si ya había un cierre de ese mercado esperando en la cola, se recupera para editarlo en vez
  // de empezar de cero: si no, quien vuelve a entrar cree que se perdió y lo llena distinto.
  const claveMercado = pdvId === '' ? ''
    : pdvId === MERCADO_OTRO
      ? (mercadoLibre.trim() === '' ? '' : claveDeMercado('', mercadoLibre))
      : claveDeMercado(pdvId, '');

  useEffect(() => {
    if (claveMercado === '') return;
    void cierreLocalDe(claveMercado, fecha).then((previo) => {
      if (!previo) return;
      setC({
        auditorDeclarado: '',
        afluencia: previo.afluencia,
        actividadAdicional: previo.actividadAdicional,
        factorExterno: previo.factorExterno,
        competenciaActiva: previo.competenciaActiva ?? '',
        observacion: previo.observacion ?? '',
      });
      setMensaje('Recuperamos el cierre que habías guardado de este mercado. Puedes corregirlo.');
    });
  }, [claveMercado, fecha]);

  function set<K extends keyof BorradorCierre>(campo: K, valor: BorradorCierre[K]) {
    setC((prev) => ({ ...prev, [campo]: valor }));
    setError('');
  }

  async function guardar() {
    if (claveMercado === '') { setError('Elige el mercado que estás cerrando.'); return; }
    const falta = loQueFaltaEnCierre(c);
    if (falta !== null) { setError(falta); return; }

    setEnviando(true);
    setError('');
    const input = {
      clientUuid: crypto.randomUUID(),
      campaignId,
      pdvId: pdvId === MERCADO_OTRO ? '' : pdvId,
      mercadoLibre: pdvId === MERCADO_OTRO ? mercadoLibre.trim() : '',
      fecha,
      afluencia: c.afluencia as 'alta' | 'normal' | 'baja',
      actividadAdicional: c.actividadAdicional === true,
      auditorDeclarado: c.auditorDeclarado,
      factorExterno: c.factorExterno,
      competenciaActiva: c.competenciaActiva.trim() === '' ? null : c.competenciaActiva.trim(),
      observacion: c.observacion.trim() === '' ? null : c.observacion.trim(),
    };
    try {
      try {
        await registrarJornadaLevantamiento(input);
        setMensaje('Jornada cerrada. Gracias.');
      } catch {
        await enqueueJornada(input);
        setMensaje('Cierre guardado en el celular (sin señal). Se sube solo.');
      }
      setC(cierreVacio());
    } catch (e) {
      setError('No se pudo guardar: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setEnviando(false);
    }
  }

  if (cargando) return <p className="p-4 text-gray-500">Cargando…</p>;

  return (
    <div className="p-4 pb-28">
      <h1 className="text-lg font-semibold">Cerrar la jornada</h1>
      <p className="mb-3 text-sm text-gray-600">
        {formatInTimeZone(new Date(), TZ, 'dd/MM/yyyy')} · una vez por mercado
      </p>

      {mensaje !== '' && (
        <p className="mb-3 rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-900">
          {mensaje}
        </p>
      )}
      {error !== '' && (
        <p className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
          {error}
        </p>
      )}

      <Bloque titulo="¿Qué mercado estás cerrando?">
        <select className="min-h-[44px] w-full rounded border px-3 text-base"
          value={pdvId} onChange={(e) => setPdvId(e.target.value)}>
          <option value="">— Elige el mercado —</option>
          {mercados.map((m) => <option key={m.id} value={m.id}>{m.nombre}</option>)}
          <option value={MERCADO_OTRO}>Otro mercado (escribirlo)</option>
        </select>
        {pdvId === MERCADO_OTRO && (
          <label className="mt-2 flex flex-col text-sm">
            ¿Qué mercado es?
            <input className="mt-1 min-h-[44px] rounded border px-3 text-base"
              placeholder="Nombre del mercado y su distrito"
              value={mercadoLibre} onChange={(e) => setMercadoLibre(e.target.value)} />
          </label>
        )}
      </Bloque>


      <Bloque titulo="¿Cómo estuvo la afluencia hoy?">
        <div className="grid grid-cols-3 gap-2">
          {AFLUENCIAS.map((a) => (
            <Opcion key={a.valor} activo={c.afluencia === a.valor}
              onClick={() => set('afluencia', a.valor)}>{a.label}</Opcion>
          ))}
        </div>
      </Bloque>

      <Bloque titulo="¿Hoy era día con alguna actividad adicional?">
        <div className="grid grid-cols-2 gap-2">
          <Opcion activo={c.actividadAdicional === true}
            onClick={() => set('actividadAdicional', true)}>Sí</Opcion>
          <Opcion activo={c.actividadAdicional === false}
            onClick={() => set('actividadAdicional', false)}>No</Opcion>
        </div>
      </Bloque>

      <Bloque titulo="¿Pasó algo que explique el día?">
        <div className="grid gap-2">
          {FACTORES_EXTERNOS.map((f) => (
            <Opcion key={f.valor} activo={c.factorExterno === f.valor}
              onClick={() => set('factorExterno', f.valor)}>{f.label}</Opcion>
          ))}
        </div>
      </Bloque>

      {/* Las dos últimas son OPCIONALES a propósito: exigirlas produciría "nada" escrito treinta
          veces, que es peor que el silencio porque parece un dato. */}
      <Bloque titulo="Competencia en el mercado">
        <label className="flex flex-col text-sm">
          ¿Había impulsadoras o activación de alguna marca competidora?
          <input className="mt-1 min-h-[44px] rounded border px-3 text-base"
            placeholder="Ej.: Veesper, 2 impulsadoras — o «ninguna»"
            value={c.competenciaActiva}
            onChange={(e) => set('competenciaActiva', e.target.value)} />
        </label>
      </Bloque>

      <Bloque titulo="Algo más que quieras dejar dicho">
        <textarea rows={3} className="w-full rounded border px-3 py-2 text-base"
          value={c.observacion}
          onChange={(e) => set('observacion', e.target.value)} />
      </Bloque>

      <p className="mb-4 text-center text-sm">
        <Link to="/movil/ficha-puesto" className="text-blue-600 underline">
          Volver a medir puestos
        </Link>
        <button type="button" className="ml-4 text-blue-600 underline"
          onClick={() => void flushJornadas()}>
          Intentar subir lo pendiente
        </button>
      </p>

      <div className="fixed inset-x-0 bottom-0 border-t bg-white p-3">
        <button type="button" onClick={() => void guardar()} disabled={enviando}
          className="min-h-[48px] w-full rounded bg-blue-600 text-base font-medium text-white disabled:opacity-50">
          {enviando ? 'Guardando…' : 'Cerrar la jornada'}
        </button>
      </div>
    </div>
  );
}
